class GridVariantSync {
  constructor(productItem) {
    this.productItem = productItem;

    this.wrapper =
      productItem.querySelector(
        '.floating-swatches-wrapper'
      );

    this.form =
      this.wrapper?.querySelector(
        '[data-product-form]'
      );

    if (
      !this.wrapper ||
      !this.form
    ) {
      return;
    }

    this.variantInput =
      this.form.querySelector(
        'input[name="id"]'
      );

    this.addButton =
      this.form.querySelector(
        '[data-add-to-cart]'
      );

    this.variantSelectors =
      this.form.querySelectorAll(
        '[data-single-option-selector]'
      );

    this.productJson =
      this.getOriginalProductJson();

    this.variants =
      this.productJson?.variants || [];

    this.selectedColorProduct =
      this.productJson;

    this.handleColorChange =
      this.handleColorChange.bind(this);

    this.init();
  }

  init() {
    /*
     * Color change only.
     *
     * Size change is handled by
     * FloatingSwatchesManager.
     */
    this.productItem.addEventListener(
      'product-color-change',
      this.handleColorChange
    );

    /*
     * Initial selected color
     */
    this.wrapper._selectedColorProduct =
      this.productJson;

    /*
     * Initial size availability
     */
    this.updateSizeAvailability();
  }

  destroy() {
    this.productItem.removeEventListener(
      'product-color-change',
      this.handleColorChange
    );

    this.wrapper =
      null;

    this.form =
      null;

    this.productItem =
      null;
  }

  getOriginalProductJson() {
    const script =
      this.productItem.querySelector(
        'script[data-product-json]'
      );

    if (!script) {
      return null;
    }

    try {
      return JSON.parse(
        script.textContent
      );
    } catch (error) {
      console.error(
        '[GridVariantSync] Failed to parse product JSON:',
        error
      );

      return null;
    }
  }

  getColorProductJson(productId) {
    const script =
      this.productItem.querySelector(
        `[data-color-product-json="${CSS.escape(
          productId
        )}"]`
      );

    if (!script) {
      console.warn(
        '[GridVariantSync] Color product JSON not found:',
        productId
      );

      return null;
    }

    try {
      return JSON.parse(
        script.textContent
      );
    } catch (error) {
      console.error(
        '[GridVariantSync] Failed to parse color product JSON:',
        error
      );

      return null;
    }
  }

  handleColorChange(event) {
    const {
      productId
    } = event.detail || {};

    if (!productId) {
      return;
    }

    const colorProduct =
      this.getColorProductJson(
        productId
      );

    if (!colorProduct) {
      return;
    }

    /*
     * Store selected color product
     */
    this.selectedColorProduct =
      colorProduct;

    this.wrapper._selectedColorProduct =
      colorProduct;

    /*
     * Replace variants
     */
    this.variants =
      colorProduct.variants || [];

    /*
     * Update Size availability only.
     */
    this.updateSizeAvailability();
  }

  getSizeOptionPosition() {
    const sizeSelector =
      Array.from(
        this.variantSelectors
      ).find(selector => {
        const optionName =
          (
            selector.dataset.optionName ||
            ''
          )
            .trim()
            .toLowerCase();

        return (
          optionName === 'size'
        );
      });

    if (!sizeSelector) {
      return null;
    }

    const position =
      parseInt(
        sizeSelector.dataset.optionPosition,
        10
      );

    if (Number.isNaN(position)) {
      return null;
    }

    return position;
  }

  updateSizeAvailability() {
    const sizePosition =
      this.getSizeOptionPosition();

    if (
      sizePosition === null
    ) {
      return;
    }

    /*
     * Available sizes for
     * current selected color.
     */
    const availableSizes =
      new Set();

    this.variants.forEach(
      variant => {
        if (!variant.available) {
          return;
        }

        const sizeValue =
          variant.options[
            sizePosition - 1
          ];

        if (sizeValue) {
          availableSizes.add(
            sizeValue
          );
        }
      }
    );

    this.variantSelectors.forEach(
      selector => {
        const optionName =
          (
            selector.dataset.optionName ||
            ''
          )
            .trim()
            .toLowerCase();

        if (
          optionName !== 'size'
        ) {
          return;
        }

        if (
          selector.tagName ===
          'SELECT'
        ) {
          this.updateSizeSelect(
            selector,
            availableSizes
          );

          return;
        }

        this.updateSizeInput(
          selector,
          availableSizes
        );
      }
    );

    this.clearUnavailableSelectedSize(
      availableSizes
    );
  }

  updateSizeInput(
    input,
    availableSizes
  ) {
    const isAvailable =
      availableSizes.has(
        input.value
      );

    input.disabled =
      !isAvailable;

    const label =
      input.id
        ? this.form.querySelector(
            `label[for="${CSS.escape(
              input.id
            )}"]`
          )
        : null;

    if (label) {
      label.classList.toggle(
        'disabled',
        !isAvailable
      );

      label.classList.toggle(
        'is-disabled',
        !isAvailable
      );
    }

    const radioButton =
      input.closest(
        '.radio__button'
      );

    if (radioButton) {
      radioButton.classList.toggle(
        'disabled',
        !isAvailable
      );

      radioButton.classList.toggle(
        'is-disabled',
        !isAvailable
      );

      radioButton.classList.toggle(
        'sold-out',
        !isAvailable
      );
    }
  }

  updateSizeSelect(
    select,
    availableSizes
  ) {
    Array.from(
      select.options
    ).forEach(option => {
      if (!option.value) {
        return;
      }

      option.disabled =
        !availableSizes.has(
          option.value
        );
    });
  }

  clearUnavailableSelectedSize(
    availableSizes
  ) {
    const checkedSize =
      this.form.querySelector(
        '[data-option-name="Size"]:checked'
      );

    if (
      checkedSize &&
      !availableSizes.has(
        checkedSize.value
      )
    ) {
      checkedSize.checked =
        false;
    }
  }
}


/*
 * Initialize one product item
 */
function initGridVariantSyncItem(
  productItem
) {
  if (!productItem) {
    return;
  }

  /*
   * Already initialized.
   */
  if (
    productItem._gridVariantSync
  ) {
    return;
  }

  const sync =
    new GridVariantSync(
      productItem
    );

  /*
   * Constructor may return early
   * if product JSON/form is not ready.
   */
  if (
    !sync.wrapper ||
    !sync.form
  ) {
    return;
  }

  productItem._gridVariantSync =
    sync;
}


/*
 * Initialize all current products
 */
function initGridVariantSync() {
  document
    .querySelectorAll(
      '[data-product-grid-item]'
    )
    .forEach(
      initGridVariantSyncItem
    );
}


/*
 * Find the actual product grid
 *
 * IMPORTANT:
 * Replace these selectors with
 * your actual product-grid selector
 * if available.
 */
function getProductGridContainer() {
  return (
    document.querySelector(
      '[data-product-grid]'
    ) ||
    document.querySelector(
      '.product-grid'
    ) ||
    document.querySelector(
      '.collection-product-list'
    ) ||
    document.body
  );
}


/*
 * Observe AJAX product changes
 */
function observeGridVariantSync() {
  const container =
    getProductGridContainer();

  if (!container) {
    return;
  }

  const observer =
    new MutationObserver(
      mutations => {
        mutations.forEach(
          mutation => {
            if (
              mutation.type !==
              'childList'
            ) {
              return;
            }

            mutation.addedNodes.forEach(
              node => {
                if (
                  node.nodeType !==
                  Node.ELEMENT_NODE
                ) {
                  return;
                }

                /*
                 * The added node itself
                 * is a product item.
                 */
                if (
                  node.matches(
                    '[data-product-grid-item]'
                  )
                ) {
                  initGridVariantSyncItem(
                    node
                  );

                  return;
                }

                /*
                 * The added node contains
                 * product items.
                 */
                node
                  .querySelectorAll(
                    '[data-product-grid-item]'
                  )
                  .forEach(
                    initGridVariantSyncItem
                  );
              }
            );
          }
        );
      }
    );

  observer.observe(
    container,
    {
      childList: true,
      subtree: true
    }
  );

  return observer;
}


/*
 * Boot
 */
function bootGridVariantSync() {
  /*
   * Initial products
   */
  initGridVariantSync();

  /*
   * AJAX products
   */
  observeGridVariantSync();
}


if (
  document.readyState ===
  'loading'
) {
  document.addEventListener(
    'DOMContentLoaded',
    bootGridVariantSync,
    {
      once: true
    }
  );
} else {
  bootGridVariantSync();
}
